import { Mail, Github, Linkedin, Download, ArrowUpRight } from "lucide-react";
import { profile } from "@/data/portfolio";
import { Reveal } from "@/lib/motion";
import { TID } from "@/constants/testIds";

const gmailCompose = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(
  profile.email
)}&su=${encodeURIComponent("Let's talk — engineering role")}&body=${encodeURIComponent(
  "Hi Jemsheena,\n\n"
)}`;

export function Contact() {
  return (
    <section id="contact" data-testid={TID.contact} className="mx-auto max-w-5xl px-5 py-20 sm:px-8 sm:py-24">
      <Reveal className="relative mx-auto max-w-2xl overflow-hidden rounded-3xl border border-border bg-card/50 p-7 sm:p-12">
        <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-primary/10 blur-3xl" />
        <div className="relative">
          <span className="kicker">05 / contact</span>
          <h2 className="mt-4 max-w-2xl font-display text-[clamp(2rem,5vw,3.5rem)] font-bold leading-[1.02] text-foreground">
            Let&apos;s build systems that stay up.
          </h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            I&apos;m currently {profile.status}. If you&apos;re hiring for backend, platform, or infrastructure work,
            I&apos;d love to talk.
          </p>

          <div className="mt-9 grid grid-cols-2 gap-2 sm:flex sm:flex-nowrap sm:items-center sm:gap-3">
            <a
              href={gmailCompose}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={TID.contactEmail}
              className="group inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary px-2 py-2 text-center font-mono text-[0.62rem] font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5 sm:w-auto sm:px-4 sm:py-3 sm:text-sm"
            >
              <Mail className="h-4 w-4" /> Get in touch
            </a>
            <a
              href={profile.resume}
              download
              data-testid={TID.contactResume}
              className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full border border-border bg-secondary/40 px-2 py-2 text-center font-mono text-[0.62rem] text-foreground transition-colors hover:border-primary/40 hover:text-primary sm:w-auto sm:px-4 sm:py-3 sm:text-sm"
            >
              <Download className="h-4 w-4" /> Download resume
            </a>
            <a
              href={profile.github}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-github"
              className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full border border-border bg-secondary/40 px-2 py-2 text-center font-mono text-[0.62rem] text-foreground transition-colors hover:border-primary/40 hover:text-primary sm:w-auto sm:px-4 sm:py-3 sm:text-sm"
            >
              <Github className="h-4 w-4" /> GitHub
            </a>
            <a
              href={profile.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-linkedin"
              className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full border border-border bg-secondary/40 px-2 py-2 text-center font-mono text-[0.62rem] text-foreground transition-colors hover:border-primary/40 hover:text-primary sm:w-auto sm:px-4 sm:py-3 sm:text-sm"
            >
              <Linkedin className="h-4 w-4" /> LinkedIn
            </a>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

export function Footer() {
  return (
    <footer data-testid={TID.footer} className="border-t border-border">
      <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-4 px-5 py-6 sm:flex-row sm:items-center sm:px-8 sm:py-8">
        <div>
          <div className="font-display text-base font-bold text-foreground sm:text-lg">{profile.name}</div>
          <div className="font-mono text-[0.7rem] text-muted-foreground sm:text-xs">{profile.headline} · {profile.location}</div>
        </div>
        <div className="flex items-center gap-4">
          <a href={profile.github} target="_blank" rel="noopener noreferrer" aria-label="GitHub" className="text-muted-foreground transition-colors hover:text-primary"><Github className="h-4 w-4 sm:h-5 sm:w-5" /></a>
          <a href={profile.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="text-muted-foreground transition-colors hover:text-primary"><Linkedin className="h-4 w-4 sm:h-5 sm:w-5" /></a>
          <a href={gmailCompose} target="_blank" rel="noopener noreferrer" aria-label={`Email ${profile.email}`} className="text-muted-foreground transition-colors hover:text-primary"><Mail className="h-4 w-4 sm:h-5 sm:w-5" /></a>
        </div>
      </div>
      <div className="mx-auto max-w-6xl px-5 pb-5 font-mono text-[0.62rem] text-muted-foreground/70 sm:px-8 sm:pb-6">
        © {new Date().getFullYear()} {profile.name} · Built with React &amp; framer-motion · Designed for reliability.
      </div>
    </footer>
  );
}
